import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, RotateCcw, Ban } from "lucide-react";
import { runsApi } from "@/api/runs";
import { eventsApi } from "@/api/events";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { StatusBadge } from "@/components/status-badge";
import { StepWaterfall } from "@/components/step-waterfall";
import { RunPanel, type RunCrumb } from "@/components/run-panel";
import { JsonViewer } from "@/components/json-viewer";
import { ConfirmDialog } from "@/components/confirm-dialog";
import { DetailSkeleton } from "@/components/detail-skeleton";
import { formatAbsolute, formatDurationMs, formatRunDuration } from "@/lib/format";
import { POLL, isRunActive } from "@/lib/poll";
import { useState } from "react";

export const Route = createFileRoute("/_authed/runs/$id")({
  component: RunDetailPage,
});

function RunDetailPage() {
  const { id } = Route.useParams();
  const qc = useQueryClient();
  const nav = useNavigate();

  const q = useQuery({
    queryKey: ["run", id],
    queryFn: () => runsApi.get(id),
    refetchInterval: (query) => {
      const d = query.state.data;
      if (!d) return POLL.runDetail;
      return isRunActive(d.status) ? POLL.runDetail : false;
    },
  });

  const eventId = q.data?.event_id;
  const triggerQ = useQuery({
    queryKey: ["event", eventId],
    queryFn: () => eventsApi.get(eventId!),
    enabled: !!eventId,
  });

  const [cancelOpen, setCancelOpen] = useState(false);
  const [replayOpen, setReplayOpen] = useState(false);
  // Drill-down stack for viewing step.call children in the side panel.
  const [panelStack, setPanelStack] = useState<RunCrumb[]>([]);

  const cancel = useMutation({
    mutationFn: () => runsApi.cancel(id),
    onSuccess: () => {
      setCancelOpen(false);
      void qc.invalidateQueries({ queryKey: ["run", id] });
    },
  });
  const replay = useMutation({
    mutationFn: () => runsApi.replay(id),
    onSuccess: (res) => {
      setReplayOpen(false);
      void qc.invalidateQueries({ queryKey: ["runs"] });
      void nav({ to: "/runs/$id", params: { id: res.new_run_id } });
    },
  });

  if (q.isLoading) return <DetailSkeleton />;
  if (q.isError || !q.data) return <div className="text-fail">Run not found</div>;

  const run = q.data;
  const isTerminal = ["completed", "failed", "cancelled"].includes(run.status);

  return (
    <div className="space-y-6">
      {/* header */}
      <div>
        <Link to="/runs" className="mb-2 inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground">
          <ArrowLeft size={13} /> Runs
        </Link>
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-center gap-3">
            <h1 className="font-display text-2xl font-semibold tracking-tight font-mono">{run.function_slug}</h1>
            <StatusBadge status={run.status} />
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" className="gap-1.5" onClick={() => setReplayOpen(true)} disabled={!isTerminal}>
              <RotateCcw size={14} /> Replay
            </Button>
            <Button variant="destructive" size="sm" className="gap-1.5" onClick={() => setCancelOpen(true)} disabled={isTerminal}>
              <Ban size={14} /> Cancel
            </Button>
          </div>
        </div>
      </div>

      {/* meta strip — queue lifecycle: queued → started (picked up) → ended */}
      <Card className="grid grid-cols-2 gap-px overflow-hidden bg-border md:grid-cols-4">
        <Meta label="Queued" value={formatAbsolute(run.started_at)} mono />
        <Meta label="Started" value={run.claimed_at ? formatAbsolute(run.claimed_at) : "—"} mono />
        <Meta label="Ended" value={run.completed_at ? formatAbsolute(run.completed_at) : "—"} mono />
        <Meta label="Delay" value={queueDelay(run.started_at, run.claimed_at)} mono />
        <Meta label="Duration" value={formatRunDuration(run.claimed_at ?? run.started_at, run.completed_at)} mono />
        <Meta label="Attempt" value={String(run.attempt)} />
        <Meta
          label="Event"
          value={run.event_name ?? "—"}
          mono
          href={run.event_id ? { to: "/events/$id", id: run.event_id } : undefined}
        />
      </Card>

      {/* trigger payload */}
      {run.event_id && (
        <Card className="overflow-hidden">
          <div className="flex items-center justify-between border-b px-5 py-3">
            <div>
              <h2 className="font-display text-[15px] font-semibold tracking-tight">Trigger</h2>
              <p className="font-mono text-xs text-muted-foreground">{run.event_name}</p>
            </div>
            {run.event_id && (
              <Link to={"/events/$id" as never} params={{ id: run.event_id } as never}
                className="text-xs font-medium text-brand hover:underline">
                View event
              </Link>
            )}
          </div>
          <div className="p-5">
            {triggerQ.isLoading ? (
              <div className="text-xs text-muted-foreground">Loading payload…</div>
            ) : triggerQ.data ? (
              <JsonViewer data={triggerQ.data.data} />
            ) : (
              <div className="text-xs text-muted-foreground">Payload unavailable</div>
            )}
          </div>
        </Card>
      )}

      {/* step waterfall */}
      <Card className="overflow-hidden">
        <div className="border-b px-5 py-3">
          <h2 className="font-display text-[15px] font-semibold tracking-tight">Steps</h2>
          <p className="text-xs text-muted-foreground">Execution timeline · click a step for its input & output</p>
        </div>
        <div className="p-4">
          <StepWaterfall
            steps={run.step_history}
            runStart={run.started_at}
            runEnd={run.completed_at}
            runStatus={run.status}
            childRuns={run.child_runs}
            onOpenChild={(child) => setPanelStack([{ id: child.id, label: child.function_slug }])}
          />
        </div>
      </Card>

      {/* run output */}
      {run.output !== undefined && run.output !== null && (
        <Card className="overflow-hidden">
          <div className="border-b px-5 py-3">
            <h2 className="font-display text-[15px] font-semibold tracking-tight">Output</h2>
          </div>
          <div className="p-5"><JsonViewer data={run.output} /></div>
        </Card>
      )}

      {/* run error */}
      {run.error && (
        <Card className="overflow-hidden ring-1 ring-inset ring-fail/25">
          <div className="border-b border-fail/20 bg-fail/5 px-5 py-3">
            <h2 className="font-display text-[15px] font-semibold tracking-tight text-fail">Error</h2>
          </div>
          <div className="space-y-2 p-5 text-sm">
            <Field label="Name"><code className="font-mono text-fail">{run.error.name}</code></Field>
            <Field label="Message">{run.error.message}</Field>
            <Field label="Retryable"><code className="font-mono">{String(run.error.retryable)}</code></Field>
            {run.error.stack && (
              <pre className="scroll-thin mt-1 overflow-auto rounded-md border bg-muted p-3 font-mono text-xs">{run.error.stack}</pre>
            )}
          </div>
        </Card>
      )}

      <ConfirmDialog
        open={cancelOpen}
        onOpenChange={setCancelOpen}
        title="Cancel this run?"
        description="The orchestrator will mark this run cancelled. If it is currently executing, the SDK response for the in-flight step will be ignored."
        confirmLabel="Cancel run"
        loading={cancel.isPending}
        onConfirm={() => cancel.mutate()}
      />
      <ConfirmDialog
        open={replayOpen}
        onOpenChange={setReplayOpen}
        title="Replay this run?"
        description="A new run will be created against the same function and event with attempt=0. The original run stays untouched."
        confirmLabel="Replay run"
        loading={replay.isPending}
        onConfirm={() => replay.mutate()}
      />

      <RunPanel stack={panelStack} onStackChange={setPanelStack} />
    </div>
  );
}

/** Time a run waited in the queue before a worker picked it up. */
function queueDelay(queuedISO: string, claimedISO?: string): string {
  if (!claimedISO) return "—";
  const ms = new Date(claimedISO).getTime() - new Date(queuedISO).getTime();
  return ms <= 0 ? "0ms" : formatDurationMs(ms);
}

function Meta({ label, value, mono, href }: {
  label: string;
  value: string;
  mono?: boolean;
  href?: { to: string; id: string };
}) {
  const body = href ? (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    <Link to={href.to as any} params={{ id: href.id } as any} className="text-brand hover:underline">{value}</Link>
  ) : value;
  return (
    <div className="bg-card px-4 py-3">
      <div className="mb-1 font-mono text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className={mono ? "font-mono text-[13px]" : "text-[13px]"}>{body}</div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex gap-2">
      <span className="w-20 shrink-0 font-mono text-xs text-muted-foreground">{label}</span>
      <span className="min-w-0 break-words">{children}</span>
    </div>
  );
}
